/**
 * Where a tab keeps its session token.
 *
 * sessionStorage is scoped to one tab, so two tabs (human and bot) never share credentials
 * through browser storage (PrimeraVersion.md §14), while a reload of the same tab resumes its
 * session. Storage can be unavailable (privacy modes), so every access is guarded and the
 * game still works for the lifetime of the page.
 */
export interface TokenStore {
  read(): string | null;
  write(token: string): void;
  clear(): void;
}

const KEY = 'webgame.session.token';

export function createTabTokenStore(storage: () => Storage = () => sessionStorage): TokenStore {
  let memory: string | null = null;
  return {
    read() {
      try {
        return storage().getItem(KEY) ?? memory;
      } catch {
        return memory;
      }
    },
    write(token) {
      memory = token;
      try {
        storage().setItem(KEY, token);
      } catch {
        // Storage unavailable: the in-memory copy keeps the session alive for this page.
      }
    },
    clear() {
      memory = null;
      try {
        storage().removeItem(KEY);
      } catch {
        // Nothing persisted to clear.
      }
    },
  };
}

export function createMemoryTokenStore(): TokenStore {
  let token: string | null = null;
  return {
    read: () => token,
    write: (value) => {
      token = value;
    },
    clear: () => {
      token = null;
    },
  };
}
